<template>
  <f7-page :page-content="false">
    <r-form ref="rform" class="page" classContent="page-content full-height" :defaultData="defaultData">
      <template v-slot:before="{ rform }">
        <f7-navbar :back-link="$t('atras')">
          <f7-nav-title>{{ $t('agenteia.documents') }}</f7-nav-title>
          <f7-nav-right>
          </f7-nav-right>
        </f7-navbar>
      </template>

      <template v-slot:default="{ rform }">
        <v-app class="full-height">
          <v-sheet height="64" class="d-flex">
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('agenteia.documents') }}</v-toolbar-title>
            </v-toolbar>
          </v-sheet>

          <v-row class="full-height doc-content" justify="space-between">
            <v-col :cols="activeElement ? 5 : 12" class="full-height">
              <v-sheet class="full-height">
                <div class="iconos-superiores">
                  <v-icon color="teal" class="icono-superior" @click="newDocument(rform)">
                    {{ 'mdi-file-plus' }}
                  </v-icon>
                  <v-icon color="orange" class="icono-superior" @click="loadDocuments">
                    {{ 'mdi-refresh' }}
                  </v-icon>
                </div>

                <input
                  ref="fileInput"
                  type="file"
                  style="display:none;"
                  accept=".pdf,.txt,.md,.html,.json"
                  @change="onFileSelected"
                />

                <v-treeview
                  v-model="tree"
                  :items="treeItems"
                  activatable
                  hoverable
                  :multiple-active="false"
                  :return-object="true"
                  item-key="id"
                  @update:active="onActiveItem"
                  :open-on-click="true"
                >
                  <template v-slot:prepend="{ item, open }">
                    <v-icon v-if="item.isDirectory">
                      {{ open ? 'mdi-folder-open' : 'mdi-folder' }}
                    </v-icon>
                    <v-icon v-else>
                      {{ item.extension === 'pdf' ? 'mdi-file-pdf-box' : 'mdi-file-document-outline' }}
                    </v-icon>
                  </template>

                  <template v-slot:label="{ item }">
                    <div class="item-label">
                      <template v-if="item.isDirectory">
                        {{ item.name }}
                      </template>
                      <template v-else>
                        <div class="item-title-row">
                          <div class="item-title">
                            <v-progress-circular
                              v-if="item.processing_status === 'EN_PROCESO'"
                              indeterminate
                              size="14"
                              width="2"
                              color="primary"
                              class="margin-right-half"
                              v-tooltip="'Indexando...'"
                            ></v-progress-circular>
                            <v-icon
                              v-else-if="item.processing_status === 'ERROR'"
                              color="red"
                              small
                              class="margin-right-half"
                              v-tooltip="'Error en la última indexación'"
                            >mdi-alert-circle</v-icon>
                            <v-icon
                              v-else-if="item.reindex_pendiente"
                              color="orange"
                              small
                              class="margin-right-half"
                              v-tooltip="(item.reindex_motivo || 'Indexado con una versión anterior.') + ' El agente no lo encuentra hasta que se reindexe.'"
                            >mdi-refresh-circle</v-icon>
                            {{ item.name }}
                          </div>
                          <div class="item-icons">
                            <v-icon color="teal" small class="item-icon" @click.stop="selectDocument(item.version_id)" v-tooltip="'Editar documento'">
                              {{ 'mdi-form-textbox' }}
                            </v-icon>
                            <v-icon color="green" small class="item-icon" @click.stop="openDocument(item)" v-tooltip="'Abrir documento'">
                              {{ 'mdi-open-in-new' }}
                            </v-icon>
                          </div>
                        </div>
                        <div class="file-time">{{ item.filename }}</div>
                      </template>
                    </div>
                  </template>
                </v-treeview>
              </v-sheet>
            </v-col>

            <v-divider vertical v-if="activeElement || hasFormContent"></v-divider>

            <v-col v-if="activeElement || hasFormContent" class="d-flex full-height">
              <v-scroll-y-transition mode="out-in">
                <div style="width: 100%;">
                  <f7-card class="elevation-3 padding-vertical-half padding-right ai-document-card" :class="{ 'ai-document-locked': isProcessing }">
                    <f7-card-content>
                      <div v-if="isProcessing" class="ai-document-processing-banner">
                        <f7-preloader :size="20" color="blue"></f7-preloader>
                        <div class="ai-document-processing-body">
                          <span>{{ processingLabel }}</span>
                          <div v-if="activeProgress && activeProgress.total" class="ai-document-progress-track">
                            <div class="ai-document-progress-fill" :style="{ width: progressPercent + '%' }"></div>
                          </div>
                        </div>
                        <f7-button small outline color="red" class="ai-document-cancel-btn" @click="cancelIndexing">
                          Cancelar
                        </f7-button>
                      </div>
                      <div v-else-if="processingError" class="ai-document-error-banner">
                        <span class="ai-document-error-text">⚠️ {{ processingError }}</span>
                        <f7-button small fill color="blue" class="ai-document-retry-btn" @click="reindexDocument(rform)">
                          Reintentar
                        </f7-button>
                      </div>
                      <div v-else-if="reindexPendiente" class="ai-document-stale-banner">
                        <span class="ai-document-stale-text">🔄 {{ reindexMotivo }} El agente no lo encuentra hasta que se reindexe.</span>
                        <f7-button small fill color="blue" class="ai-document-retry-btn" @click="lanzarReindexado">
                          Reindexar
                        </f7-button>
                      </div>
                      <div class="ai-document-header">
                        <div>
                          <div class="ai-document-title">{{ rform.formData.title || 'Documento sin título' }}</div>
                          <div class="ai-document-subtitle">
                            {{ selectedMateriaLabel }}
                            <span v-if="rform.formData.version_id"> - {{ rform.formData.version_id }}</span>
                          </div>
                        </div>
                        <div class="ai-document-actions">
                          <f7-button small tonal color="blue" :disabled="isProcessing" @click="$refs.fileInput.click()">{{ $t('agenteia.selectFile') }}</f7-button>
                          <f7-button small fill color="green" :disabled="isProcessing" @click="saveDocument(rform)">{{ $t('common.guardar') }}</f7-button>
                        </div>
                      </div>

                      <div class="ai-document-section-title">Identificación</div>
                      <f7-row no-gap class="list list-form ai-document-form-row" style="z-index: 10;">
                        <r-input class="col-100 medium-33" :form="rform" type="text" name="document_id" floating-label :label="$t('agenteia.documentId')"></r-input>
                        <r-input class="col-100 medium-33" :form="rform" type="text" name="version_id" floating-label :label="$t('agenteia.versionId')"></r-input>
                        <r-autocomplete
                          class="col-100 medium-34"
                          :dbAdapter="Materia"
                          :form="rform"
                          name="materia"
                          floating-label
                          :label="$t('servicios.materia')"
                          labelProp="nombre"
                          prefixProp="codigo"
                          :shownFields="['codigo', 'codigoExpediente']"
                        ></r-autocomplete>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-autocomplete-m2m
                          class="col-100"
                          :dbAdapter="Servicio"
                          :form="rform"
                          name="servicios"
                          floating-label
                          :label="$t('agenteia.servicios')"
                          labelProp="nombre"
                          prefixProp="codigo"
                          idProp="codigo"
                          idFieldDetail="id"
                          :chipsView="true"
                        ></r-autocomplete-m2m>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-autocomplete-m2m
                          class="col-100"
                          :dbAdapter="Formato"
                          :form="rform"
                          name="formatos"
                          floating-label
                          :label="$t('agenteia.formatos')"
                          labelProp="nombre"
                          prefixProp="codigo"
                          idProp="codigo"
                          idFieldDetail="id"
                          :chipsView="true"
                        ></r-autocomplete-m2m>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-autocomplete-m2m
                          class="col-100"
                          :dbAdapter="Comunidad"
                          :form="rform"
                          name="comunidades"
                          floating-label
                          :label="$t('agenteia.comunidades')"
                          labelProp="nombre"
                          prefixProp="codigo"
                          idProp="codigo"
                          idFieldDetail="id"
                          :chipsView="true"
                        ></r-autocomplete-m2m>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100" :form="rform" type="text" name="title" floating-label :label="$t('agenteia.title')"></r-input>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <!-- Lista cerrada y no texto libre: el motor de generación distingue
                             DIRECTRIZ de todo lo demás comparando el valor exacto, así que un
                             "DIRECTIZ" mal tecleado no daría error — se trataría en silencio como
                             normativa corriente y la directriz nunca se aplicaría. -->
                        <!-- Sin boton de limpiar: `rInput` lo pone por defecto y limpiar un select
                             escribe `null`, con lo que la lista cerrada dejaria de serlo. Lo caro no
                             es dejar un documento sin tipo, sino poder VACIAR el de una directriz ya
                             creada: se convierte en normativa corriente sin decir nada, que es justo
                             lo que este desplegable venia a evitar. -->
                        <r-input class="col-100 medium-25" :form="rform" type="select" name="tipo" floating-label :clearButton="false" :label="$t('agenteia.tipo')">
                          <option value="REGLAMENTO">Reglamento (normativa citable)</option>
                          <option value="DIRECTRIZ">Directriz (instrucciones de generación)</option>
                        </r-input>
                        <r-input class="col-100 medium-25" :form="rform" type="text" name="estado" floating-label :label="$t('agenteia.estado')"></r-input>
                        <r-input class="col-100 medium-25" :form="rform" type="text" name="origen" floating-label :label="$t('agenteia.origen')"></r-input>
                        <r-input class="col-100 medium-25" :form="rform" type="date" name="vigente_desde" floating-label :label="$t('agenteia.vigenteDesde')"></r-input>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100 medium-25" :form="rform" type="date" name="vigente_hasta" floating-label :label="$t('agenteia.vigenteHasta')"></r-input>
                      </f7-row>

                      <div class="ai-document-section-title">Contexto para IA</div>
                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100" :form="rform" type="textarea" name="descripcion" floating-label :label="$t('agenteia.descripcion')"></r-input>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100" :form="rform" type="textarea" name="comentario_agente" floating-label :label="$t('agenteia.comentarioAgente')"></r-input>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input-badge
                          class="col-100"
                          :form="rform"
                          name="tags"
                          floating-label
                          :label="$t('agenteia.tags')"
                          :predefinidos="TAGS_DOCUMENTOS"
                          :uppercase="true"
                          :allowDuplicates="false"
                          :placeholder="$t('agenteia.tags')"
                        ></r-input-badge>
                      </f7-row>

                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100" :form="rform" type="text" name="path" floating-label readonly :label="$t('agenteia.path')"></r-input>
                      </f7-row>

                      <div class="ai-document-section-title">Indexación</div>
                      <f7-row no-gap class="list list-form ai-document-form-row">
                        <r-input class="col-100 medium-40" :form="rform" type="text" name="filename" floating-label readonly :label="$t('agenteia.filename')"></r-input>
                        <r-input class="col-100 medium-20" :form="rform" type="number" name="page_count" floating-label readonly :label="$t('agenteia.pageCount')"></r-input>
                        <r-input class="col-100 medium-20" :form="rform" type="number" name="chunk_count" floating-label readonly :label="$t('agenteia.chunkCount')"></r-input>
                        <r-input class="col-100 medium-20" :form="rform" type="text" name="username" floating-label readonly :label="$t('usuario')"></r-input>
                      </f7-row>

                      <div class="ai-document-footer">
                        <div class="ai-document-footer-text">
                          <div><strong>{{ $t('agenteia.selectedFile') }}:</strong> {{ selectedFileName || $t('agenteia.noFileSelected') }}</div>
                          <div v-if="rform.formData.path"><strong>{{ $t('agenteia.folder') }}:</strong> {{ getFolder(rform.formData.path) }}</div>
                        </div>
                        <div class="ai-document-actions">
                          <f7-button v-if="rform.formData.path" class="margin-right-half" color="teal" @click="openDocument(rform.formData)">{{ $t('agenteia.open') }}</f7-button>
                          <f7-button v-if="rform.formData.path" class="margin-right-half" color="green" @click="downloadDocument(rform.formData)">{{ $t('agenteia.download') }}</f7-button>
                          <f7-button v-if="rform.formData.version_id" color="red" @click="deleteDocument(rform.formData)">{{ $t('common.eliminar') }}</f7-button>
                        </div>
                      </div>
                    </f7-card-content>
                  </f7-card>
                </div>
              </v-scroll-y-transition>
            </v-col>
          </v-row>
        </v-app>
      </template>
    </r-form>
  </f7-page>
</template>

<script>
import rForm from './../../components/rForm.vue';
import rInput from './../../components/rInput.vue';
import rAutocomplete from './../../components/rAutocomplete.vue';
import rAutocompleteM2m from './../../components/rAutocompleteM2m.vue';
import rInputBadge from './../../components/rInputBadge.vue';
import { TAGS_DOCUMENTOS } from './config.js';
import AiAgentDocuments from './../../js/db/AiAgentDocuments.js';
import AiDocumentsEvents from './../../js/db/AiDocumentsEvents.js';
import Materia from './../../js/db/Materia.js';
import Servicio from './../../js/db/Servicio.js';
import Formato from './../../js/db/Formato.js';
import Comunidad from './../../js/db/Comunidad.js';
import preloaderDeLaSesion from './../../js/utils/preloaderDeLaSesion.js';

export default {
  components: {
    rAutocomplete,
    rAutocompleteM2m,
    rForm,
    rInput,
    rInputBadge
  },
  data() {
    return {
      TAGS_DOCUMENTOS,
      AiAgentDocuments,
      Materia,
      Servicio,
      Formato,
      Comunidad,
      tree: [],
      activeElement: false,
      documents: [],
      // Progreso de indexación por version_id: { fase, done, total, etaMs } (llega por SSE).
      indexProgress: {},
      selectedFile: null,
      selectedFileName: '',
      // Estado de la PANTALLA, no del documento: dentro de `defaultData` viajaria al API como un
      // campo mas del catalogo. Solo sirve para no repetir la confirmacion de transversal.
      avisadoSinMateria: false,
      defaultData: {
        document_id: '',
        version_id: '',
        title: '',
        descripcion: '',
        comentario_agente: '',
        materia: {},
        servicios: [],
        formatos: [],
        comunidades: [],
        tipo: 'REGLAMENTO',
        estado: 'VIGENTE',
        origen: 'BOE',
        vigente_desde: '',
        vigente_hasta: '',
        tags: [],
        path: '',
        filename: '',
        page_count: 0,
        chunk_count: 0,
        username: ''
      }
    };
  },
  computed: {
    hasFormContent() {
      const formData = this.$refs.rform?.formData;
      if (!formData) {
        return false;
      }
      return !!(formData.document_id || formData.version_id || formData.title || this.selectedFile);
    },
    selectedMateriaLabel() {
      const materia = this.$refs.rform?.formData?.materia;
      if (materia && materia.codigo) {
        return materia.codigo;
      }
      if (typeof materia === 'string' && materia) {
        return materia;
      }
      return 'Sin materia';
    },
    documentIndex() {
      const index = {};
      this.documents.forEach((item) => {
        index[item.version_id] = item;
      });
      return index;
    },
    treeItems() {
      const grouped = {};
      this.documents.forEach((item) => {
        const materia = item.materia || 'SIN_MATERIA';
        if (!grouped[materia]) {
          grouped[materia] = {
            id: `materia-${materia}`,
            name: materia,
            isDirectory: true,
            children: []
          };
        }
        grouped[materia].children.push({
          id: item.version_id,
          version_id: item.version_id,
          name: item.title || item.filename || item.document_id,
          filename: item.filename,
          path: item.path,
          extension: (item.extension || '').toLowerCase(),
          tipo: item.tipo,
          descripcion: item.descripcion,
          document_id: item.document_id,
          processing_status: item.processing_status,
          reindex_pendiente: item.reindex_pendiente,
          reindex_motivo: item.reindex_motivo,
          isDirectory: false
        });
      });
      return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
    },
    isProcessing() {
      return !!(this.activeElement && this.activeElement.processing_status === 'EN_PROCESO');
    },
    processingError() {
      return this.activeElement && this.activeElement.processing_status === 'ERROR'
        ? (this.activeElement.processing_error || 'Error indexando el documento.')
        : '';
    },
    /**
     * El documento está indexado con una versión anterior del indexador o con otro modelo de
     * embeddings. Importa enseñarlo porque el fallo es mudo: la búsqueda filtra por modelo, así
     * que un documento así no da error, simplemente el agente no lo encuentra.
     */
    reindexPendiente() {
      return !!(this.activeElement && this.activeElement.reindex_pendiente && !this.isProcessing);
    },
    reindexMotivo() {
      return (this.activeElement && this.activeElement.reindex_motivo)
        || 'Indexado con una versión anterior.';
    },
    documentosPorReindexar() {
      return this.documents.filter((d) => d.reindex_pendiente).length;
    },
    activeProgress() {
      const versionId = this.activeElement && this.activeElement.version_id;
      return versionId ? (this.indexProgress[versionId] || null) : null;
    },
    progressPercent() {
      const p = this.activeProgress;
      if (!p || !p.total) {
        return 0;
      }
      return Math.min(100, Math.round((p.done / p.total) * 100));
    },
    processingLabel() {
      const p = this.activeProgress;
      if (!p) {
        return 'Indexando documento. Espera unos minutos…';
      }

      const titulo = {
        PREPARANDO: 'Preparando el documento',
        MARKDOWN: 'Leyendo el documento',
        EMBEBIENDO: 'Indexando documento',
        GUARDANDO: 'Guardando'
      }[p.fase] || 'Indexando documento';

      if (!p.total) {
        return `${titulo}…`;
      }

      let texto = `${titulo}… ${p.done}/${p.total}`;
      if (p.etaMs != null && p.done < p.total) {
        texto += ` · ${this.formatEta(p.etaMs)} restante aprox.`;
      }
      return texto;
    }
  },
  mounted() {
    this.loadDocuments();
    this._documentEventCallback = this._onDocumentEvent.bind(this);
    AiDocumentsEvents.subscribe(this._documentEventCallback);
  },
  beforeDestroy() {
    if (this._documentEventCallback) {
      AiDocumentsEvents.unsubscribe(this._documentEventCallback);
      this._documentEventCallback = null;
    }
    this._pararVigilancia();
  },
  watch: {
    isProcessing(procesando) {
      if (procesando) {
        this._vigilarIndexado();
      } else {
        this._pararVigilancia();
      }
    }
  },
  methods: {
    /**
     * Respaldo del SSE mientras un documento se indexa.
     *
     * El evento final es el ÚNICO que cierra el banner, y viaja por una conexión compartida que no
     * reenvía lo que ya pasó: basta una reconexión, una recarga o abrir la pantalla desde otro
     * sitio para perderlo y quedarse "indexando" para siempre. Preguntando por el estado cada poco
     * la tarjeta se cierra igual, aunque el evento no llegue.
     */
    _vigilarIndexado() {
      this._pararVigilancia();
      const self = this;
      this._vigilanciaIndexado = setInterval(function() {
        const versionId = self.activeElement && self.activeElement.version_id;
        if (!versionId) {
          self._pararVigilancia();
          return;
        }
        AiAgentDocuments.get({ itemId: versionId }, true).then(function(res) {
          if (!res || res.processing_status === 'EN_PROCESO') {
            return;
          }
          // Entre la consulta y su respuesta el usuario ha podido cambiar de documento: aplicarla
          // devolvería la tarjeta al anterior y dejaría el formulario hablando de otro.
          const seleccionado = self.activeElement && self.activeElement.version_id;
          if (seleccionado !== versionId) {
            return;
          }
          self.$delete(self.indexProgress, versionId);
          self.loadDocuments();
          // Se aplica el documento que ya se acaba de traer, en vez de volver a pedirlo con
          // selectDocument: esa segunda consulta abriría de nuevo la misma carrera, porque su
          // respuesta se aplica sin mirar si el usuario ha cambiado de documento entretanto.
          // Hay que repasar el formulario, no solo activeElement, para que se vean las páginas y
          // los fragmentos del indexado recién terminado.
          self.selectedFile = null;
          self.selectedFileName = '';
          self.fillForm(res);
          self.activeElement = res;
        }).catch(function() { /* un fallo suelto no debe cortar la vigilancia */ });
      }, 15000);
    },
    _pararVigilancia() {
      if (this._vigilanciaIndexado) {
        clearInterval(this._vigilanciaIndexado);
        this._vigilanciaIndexado = null;
      }
    },
    _onDocumentEvent(payload) {
      if (!payload) {
        return;
      }
      const versionId = payload.version_id || payload.versionId;

      // Evento de progreso: actualiza la barra "done/total" + ETA SIN recargar el catálogo
      // (llegan varios por documento; recargar en cada bloque sería innecesario).
      if (payload.status === 'PROGRESS') {
        if (versionId) {
          this.$set(this.indexProgress, versionId, {
            fase: payload.fase || 'EMBEBIENDO',
            done: payload.done,
            total: payload.total,
            etaMs: payload.eta_ms != null ? payload.eta_ms : payload.etaMs
          });
        }
        return;
      }

      // Estado final (OK/ERROR): limpia el progreso y recarga el catálogo.
      if (versionId) {
        this.$delete(this.indexProgress, versionId);
      }
      const currentVersionId = this.activeElement && this.activeElement.version_id;
      this.loadDocuments();
      if (versionId && versionId === currentVersionId) {
        this.selectDocument(versionId);
      }
      this._avisarResultado(payload);
    },
    /**
     * Deja constancia de lo que ha hecho el indexado.
     *
     * Un documento cuyo .md ya está generado se indexa en un par de segundos: las fases pasan
     * demasiado deprisa para leerlas y la pantalla se queda igual que estaba, sin decir si ha
     * ido bien. Este aviso es la única señal que queda de un trabajo tan corto.
     */
    _avisarResultado(payload) {
      if (payload.status === 'CANCELLED') {
        this.$f7.toast.create({ text: 'Indexación cancelada.', position: 'center', closeTimeout: 3000 }).open();
        return;
      }
      if (payload.status === 'ERROR') {
        this.$f7.toast.create({
          text: `No se ha podido indexar: ${payload.error || 'error desconocido'}`,
          position: 'center',
          closeTimeout: 5000
        }).open();
        return;
      }

      const indexado = payload.indexed || {};
      const partes = [];
      if (indexado.chunkCount) {
        partes.push(`${indexado.chunkCount} fragmentos`);
      }
      if (indexado.pageCount) {
        partes.push(`${indexado.pageCount} páginas`);
      }
      if (indexado.durationMs) {
        partes.push(`en ${this.formatEta(indexado.durationMs)}`);
      }
      this.$f7.toast.create({
        text: partes.length ? `Documento indexado: ${partes.join(' · ')}` : 'Documento indexado.',
        position: 'center',
        closeTimeout: 4000
      }).open();
    },
    formatEta(ms) {
      const totalSec = Math.max(0, Math.round(ms / 1000));
      if (totalSec < 60) {
        return `${totalSec} s`;
      }
      const min = Math.floor(totalSec / 60);
      const sec = totalSec % 60;
      return sec ? `${min} min ${sec} s` : `${min} min`;
    },
    loadDocuments() {
      const self = this;
      AiAgentDocuments.searchCatalog({
        limit: 500,
        order: 'materia',
        orderDir: 'ASC',
        filter: {}
      }, true).then(function(res) {
        self.documents = (res.list || []).sort((a, b) => {
          const materiaA = a.materia || '';
          const materiaB = b.materia || '';
          if (materiaA !== materiaB) {
            return materiaA.localeCompare(materiaB);
          }
          return (a.title || '').localeCompare(b.title || '');
        });
      }).catch(self.catchError);
    },
    onActiveItem(e) {
      if (e && e.length) {
        const raw = e[0];
        const item = typeof raw === 'string' ? this.documentIndex[raw] : raw;
        if (item && !item.isDirectory) {
          this.selectDocument(item.version_id || item.id);
          return;
        }
      }
      this.activeElement = false;
    },
    selectDocument(versionId) {
      const self = this;
      AiAgentDocuments.get({ itemId: versionId }, true).then(function(res) {
        self.selectedFile = null;
        self.selectedFileName = '';
        self.fillForm(res);
        self.activeElement = res;
      }).catch(self.catchError);
    },
    fillForm(item) {
      const formData = this.$refs.rform.formData;
      const toCodeObjects = (arr) => Array.isArray(arr)
        ? arr.map(codigo => ({ codigo, nombre: codigo }))
        : [];
      formData.document_id = item.document_id || '';
      formData.version_id = item.version_id || '';
      formData.title = item.title || '';
      formData.descripcion = item.descripcion || '';
      formData.comentario_agente = item.comentario_agente || '';
      formData.materia = item.materia ? { codigo: item.materia, nombre: item.materia } : {};
      formData.servicios = toCodeObjects(item.servicios);
      formData.formatos = toCodeObjects(item.formatos);
      formData.comunidades = toCodeObjects(item.comunidades);
      formData.tipo = item.tipo || 'REGLAMENTO';
      formData.estado = item.estado || 'VIGENTE';
      formData.origen = item.origen || '';
      formData.vigente_desde = item.vigente_desde || '';
      formData.vigente_hasta = item.vigente_hasta || '';
      formData.tags = Array.isArray(item.tags) ? item.tags.slice() : [];
      formData.path = item.path || '';
      formData.filename = item.filename || '';
      formData.page_count = item.page_count || 0;
      formData.chunk_count = item.chunk_count || 0;
      formData.username = item.username || '';

      this._hydrateCatalogList(Servicio, item.servicios, 'servicios');
      this._hydrateCatalogList(Formato, item.formatos, 'formatos');
      this._hydrateCatalogList(Comunidad, item.comunidades, 'comunidades');
      this._hydrateMateria(item.materia);
    },
    _hydrateCatalogList(model, codes, fieldName) {
      const self = this;
      if (!Array.isArray(codes) || !codes.length) {
        return;
      }
      model.getList({
        // OJO: los FilterDTO del backend no aceptan operadores Mongo ({$in}) — el campo
        // "codigo" es String y Jackson revienta. La lista va por el campo "codigos" (IN).
        filter: { codigos: codes },
        limit: Math.max(codes.length, 50)
      }, true).then(function(res) {
        const list = (res && res.list) || [];
        const byCode = {};
        list.forEach(function(it) {
          if (it && it.codigo) {
            byCode[it.codigo] = it;
          }
        });
        const formData = self.$refs.rform && self.$refs.rform.formData;
        if (!formData) {
          return;
        }
        formData[fieldName] = codes.map(function(c) {
          return byCode[c] || { codigo: c, nombre: c };
        });
      }).catch(function() {
      });
    },
    _hydrateMateria(codigo) {
      const self = this;
      if (!codigo) {
        return;
      }
      Materia.getList({
        filter: { codigo: codigo },
        limit: 1
      }, true).then(function(res) {
        const list = (res && res.list) || [];
        if (!list.length) {
          return;
        }
        const formData = self.$refs.rform && self.$refs.rform.formData;
        if (!formData) {
          return;
        }
        formData.materia = list[0];
      }).catch(function() {
      });
    },
    newDocument(rform) {
      this.selectedFile = null;
      this.selectedFileName = '';
      this.activeElement = { nuevo: true };
      Object.keys(rform.formData).forEach((key) => {
        rform.formData[key] = this.defaultData[key];
      });
      rform.formData.materia = {};
      rform.formData.servicios = [];
      rform.formData.formatos = [];
      rform.formData.comunidades = [];
    },
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      this.selectedFile = file || null;
      this.selectedFileName = file ? file.name : '';
      if (!file) {
        return;
      }
      const extension = file.name.split('.').pop();
      const filename = file.name;
      const title = file.name.replace(/\.[^.]+$/, '');
      const formData = this.$refs.rform.formData;
      this.activeElement = { nuevo: true };
      if (!formData.version_id) {
        formData.version_id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      }
      if (!formData.document_id) {
        formData.document_id = formData.version_id;
      }
      if (!formData.title) {
        formData.title = title;
      }
      formData.filename = filename;
      formData.extension = extension;
    },
    saveDocument(rform) {
      const payload = this.buildPayload(rform.formData);
      if (!payload.version_id) {
        this.$f7.dialog.alert('La version es obligatoria.');
        return;
      }
      if (!payload.title) {
        this.$f7.dialog.alert('El titulo es obligatorio.');
        return;
      }
      // Sin materia el documento es TRANSVERSAL: entra en el contexto de todos los formatos de
      // todas las materias. Es una funcionalidad, no un descuido —el corpus horizontal existe para
      // esto—, pero el caso más frecuente de campo vacío sigue siendo el olvido, y la diferencia
      // entre las dos cosas no se ve en ninguna pantalla después. Por eso se pregunta.
      if (!payload.materia && !this.avisadoSinMateria) {
        const self = this;
        this.$f7.dialog.confirm(
          'Este documento no tiene materia, así que se aplicará a <b>todos los formatos de todas '
          + 'las materias</b>. ¿Es lo que quieres?',
          'Documento transversal',
          function () {
            self.avisadoSinMateria = true;
            self.saveDocument(rform);
          });
        return;
      }
      this.avisadoSinMateria = false;

      if (this.selectedFile) {
        this.uploadDocument(payload);
        return;
      }
      if (!payload.path) {
        this.$f7.dialog.alert('Selecciona un fichero para crear el documento.');
        return;
      }
      this.updateDocument(payload);
    },
    uploadDocument(payload) {
      const self = this;
      // #663 · La espera de la sesión: si la sesión cambia en vuelo, la frontera calla el `then` que la apagaba.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      AiAgentDocuments.uploadAndIndex(this.selectedFile, payload).then(function(res) {
        espera.cerrar();
        self.$f7.toast.create({ text: 'Documento subido e indexado.', position: 'center', closeTimeout: 2000 }).open();
        self.selectedFile = null;
        self.selectedFileName = '';
        self.loadDocuments();
        const versionId = (((res || {}).indexacion || {}).document || {}).version_id || payload.version_id;
        self.selectDocument(versionId);
      }).catch(function(error) { self.catchError(error, espera); });
    },
    /**
     * Relanza la indexación del documento seleccionado sin tocar sus metadatos.
     *
     * No reutiliza reindexDocument(rform), que va por updateDocument: aquel solo reindexa si el
     * fichero ha cambiado, y aquí el fichero es el mismo — lo viejo es el indexado.
     */
    lanzarReindexado() {
      const self = this;
      const versionId = this.activeElement && this.activeElement.version_id;
      if (!versionId) {
        return;
      }
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      AiAgentDocuments.reindex(versionId).then(function() {
        espera.cerrar();
        self.$f7.toast.create({ text: 'Reindexando el documento…', position: 'center', closeTimeout: 2000 }).open();
        // El estado pasa a EN_PROCESO en servidor; al releerlo arranca el banner de progreso y,
        // con él, la vigilancia que cierra la tarjeta cuando termina.
        self.loadDocuments();
        self.selectDocument(versionId);
      }).catch(function(error) {
        espera.cerrar();
        self.catchError(error, espera);
      });
    },
    cancelIndexing() {
      const self = this;
      const versionId = this.activeElement && this.activeElement.version_id;
      if (!versionId) {
        return;
      }
      this.$f7.dialog.confirm('¿Detener la indexación de este documento?', 'Cancelar indexado', function () {
        const espera = preloaderDeLaSesion(self);
        espera.mostrar();
        AiAgentDocuments.cancelIndex(versionId).then(function() {
          espera.cerrar();
          // La cancelación se aplica en el siguiente bloque (hasta ~la tasa de indexado);
          // cuando aborta, llega el estado final por SSE y la tarjeta pasa a ERROR/Reintentar.
          self.$f7.toast.create({ text: 'Cancelando la indexación…', position: 'center', closeTimeout: 2000 }).open();
        }).catch(function(error) { self.catchError(error, espera); });
      });
    },
    reindexDocument(rform) {
      // Relanza la indexación de un documento en ERROR (p. ej. indexado interrumpido por
      // reinicio). El fichero ya está en servidor (path), así que no hay que volver a subirlo:
      // el backend detecta el estado ERROR y fuerza el reindexado.
      const payload = this.buildPayload(rform.formData);
      if (!payload.version_id) {
        return;
      }
      if (!payload.path) {
        this.$f7.dialog.alert('No se encuentra el fichero en servidor para reindexar. Vuelve a subirlo.');
        return;
      }
      this.updateDocument(payload);
    },
    updateDocument(payload) {
      const self = this;
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      AiAgentDocuments.updateDocument(payload.version_id, payload).then(function() {
        espera.cerrar();
        self.$f7.toast.create({ text: 'Documento actualizado e reindexado.', position: 'center', closeTimeout: 2000 }).open();
        self.loadDocuments();
        self.selectDocument(payload.version_id);
      }).catch(function(error) { self.catchError(error, espera); });
    },
    deleteDocument(formData) {
      const self = this;
      if (!formData.version_id) {
        return;
      }
      self.$f7.dialog.confirm('Se eliminara el documento fisico y su indexacion. ¿Continuar?', 'Eliminar', function () {
        const espera = preloaderDeLaSesion(self);
        espera.mostrar();
        AiAgentDocuments.deleteDocument(formData.version_id, true).then(function() {
          espera.cerrar();
          self.$f7.toast.create({ text: 'Documento eliminado.', position: 'center', closeTimeout: 2000 }).open();
          self.newDocument(self.$refs.rform);
          self.loadDocuments();
        }).catch(function(error) { self.catchError(error, espera); });
      });
    },
    buildPayload(formData) {
      const toCodeList = (arr) => Array.isArray(arr)
        ? arr.map(item => (item && item.codigo) ? item.codigo : item).filter(Boolean)
        : [];
      return {
        document_id: formData.document_id,
        version_id: formData.version_id,
        title: formData.title,
        descripcion: formData.descripcion,
        comentario_agente: formData.comentario_agente,
        materia: formData.materia && formData.materia.codigo ? formData.materia.codigo : (typeof formData.materia === 'string' && formData.materia ? formData.materia : null),
        servicios: toCodeList(formData.servicios),
        formatos: toCodeList(formData.formatos),
        comunidades: toCodeList(formData.comunidades),
        tipo: formData.tipo,
        estado: formData.estado,
        origen: formData.origen,
        vigente_desde: formData.vigente_desde,
        vigente_hasta: formData.vigente_hasta,
        tags: (Array.isArray(formData.tags) ? formData.tags : []).map(t => String(t).trim()).filter(Boolean),
        path: formData.path,
        filename: formData.filename
      };
    },
    openDocument(item) {
      const self = this;
      if (item && item.path) {
        window.open(AiAgentDocuments.getDownloadUrl(item), '_blank');
        return;
      }
      if (item && item.version_id) {
        AiAgentDocuments.get({ itemId: item.version_id }, true).then(function(res) {
          window.open(AiAgentDocuments.getDownloadUrl(res), '_blank');
        }).catch(self.catchError);
      }
    },
    downloadDocument(item) {
      const self = this;
      if (item && item.path) {
        window.open(AiAgentDocuments.getDownloadUrl(item), '_blank');
        return;
      }
      if (item && item.version_id) {
        AiAgentDocuments.get({ itemId: item.version_id }, true).then(function(res) {
          window.open(AiAgentDocuments.getDownloadUrl(res), '_blank');
        }).catch(self.catchError);
      }
    },
    getFolder(path) {
      if (!path) {
        return '';
      }
      const idx = path.lastIndexOf('/');
      return idx >= 0 ? path.substring(0, idx) : path;
    },
    // Con `espera`, el error es de una operación que puso la suya y se suelta solo ésa; sin ella,
    // queda el apagado global de siempre para quien llama sin haber encendido nada propio.
    catchError(error, espera) {
      const self = this;
      if (espera) espera.cerrar(); else self.$f7.preloader.hide();
      const message = typeof error === 'string' ? error : (error && error.error) || 'Se ha producido un error.';
      this.$f7.dialog.alert(message);
    }
  }
};
</script>

<style scoped>
.iconos-superiores {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0 12px;
  width: auto;
  max-width: max-content;
}

.icono-superior {
  cursor: pointer;
  margin-right: 0;
  flex: 0 0 auto !important;
  width: auto !important;
}

.item-label {
  cursor: pointer;
}

.item-title {
  font-weight: 600;
  line-height: 1.3;
}

.item-title-row {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
}

.file-time {
  font-size: 12px;
  color: #6c757d;
  line-height: 1.25;
}

.item-icons {
  display: inline-flex;
  gap: 2px;
  align-items: center;
}

.item-icon {
  cursor: pointer;
}

:deep(.v-treeview-node__root) {
  gap: 6px;
}

:deep(.v-treeview-node__label) {
  flex: 1 1 auto;
  min-width: 0;
}

.ai-document-card {
  margin: 0;
  position: relative;
}

.ai-document-card.ai-document-locked :deep(.f7-card-content),
.ai-document-card.ai-document-locked :deep(.list-form),
.ai-document-card.ai-document-locked :deep(.r-input),
.ai-document-card.ai-document-locked :deep(.r-autocomplete),
.ai-document-card.ai-document-locked :deep(input),
.ai-document-card.ai-document-locked :deep(textarea) {
  pointer-events: none;
}

.ai-document-card.ai-document-locked :deep(.list-form) {
  opacity: 0.65;
}

.ai-document-processing-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e8f1ff;
  border: 1px solid #b8d4ff;
  color: #22355b;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-weight: 500;
}

.ai-document-processing-body {
  flex: 1;
  min-width: 0;
}

.ai-document-cancel-btn {
  flex-shrink: 0;
}

.ai-document-progress-track {
  margin-top: 6px;
  width: 100%;
  height: 6px;
  background: #cfe0ff;
  border-radius: 3px;
  overflow: hidden;
}

.ai-document-progress-fill {
  height: 100%;
  background: #2f6bff;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.ai-document-error-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffe9e9;
  border: 1px solid #ffb3b3;
  color: #8b1a1a;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.ai-document-error-text {
  flex: 1;
  min-width: 0;
}

/* Aviso de indexado desfasado: en ámbar, no en rojo. No hay nada roto, hay algo que quedó atrás. */
.ai-document-stale-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff6e5;
  border: 1px solid #ffd699;
  color: #8a5a00;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
}

.ai-document-stale-text {
  flex: 1;
  min-width: 0;
}

.ai-document-retry-btn {
  flex-shrink: 0;
}


.ai-document-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;
}

.ai-document-title {
  font-size: 20px;
  font-weight: 700;
  color: #22355b;
}

.ai-document-subtitle {
  color: #6d7f98;
  margin-top: 4px;
}

.ai-document-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.ai-document-section-title {
  margin: 14px 0 8px 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #617892;
}

.ai-document-form-row {
  row-gap: 6px;
}

.ai-document-card :deep(.list-form) {
  margin: 0;
}

.ai-document-card :deep(.item-content),
.ai-document-card :deep(.item-input),
.ai-document-card :deep(.item-inner),
.ai-document-card :deep(.item-input-wrap) {
  min-width: 0;
}

.ai-document-card :deep(textarea) {
  min-height: 90px;
}

.ai-document-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 18px;
  padding-top: 12px;
  border-top: 1px solid #e8edf5;
}

.ai-document-footer-text {
  min-width: 0;
}

@media (max-width: 1100px) {
  .ai-document-header,
  .ai-document-footer {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
